import { api, unwrap } from "@lumberset/api-client";
import {
  ago,
  ChangeOrderStatus,
  CustomerRunStatus,
  date,
  money,
  ProjectStatus,
  QuoteStatus,
  RfqStatus,
  stageLabels,
} from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  Drawer,
  DropdownMenu,
  EmptyState,
  Field,
  IconButton,
  Input,
  ProgressBar,
  SectionHeader,
  SectionRow,
  Table,
  Textarea,
  TwoColumn,
  toast,
} from "@lumberset/ui";
import {
  CreditCard,
  DocumentText,
  EllipsisHorizontal,
  Photo,
  Plus,
  UserGroup,
} from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  AddCardDialog,
  type CardPurpose,
  cardNeeded,
  refusedStart,
  useCardReturn,
} from "../../../../components/add-card";
import { Crumbs } from "../../../../components/crumbs";
import { UploadDrawings } from "../../../../components/upload-drawings";
import {
  billingSummaryQuery,
  changeOrdersQuery,
  projectQuery,
  rfqsQuery,
} from "../../../../lib/queries";
import { ProjectCustomer } from "./-customer";

interface ProjectSearch {
  /**
   * What to pick up once a card is on file: `change-order` opens a new change
   * order, a Plan Set id starts its takeoff.
   */
  resume?: string;
  /** Back from Stripe's card page. */
  card?: "saved" | "canceled";
}

export const Route = createFileRoute("/_app/projects/$id/")({
  validateSearch: (search: Record<string, unknown>): ProjectSearch => ({
    ...(typeof search.resume === "string" && /^(change-order|pset_[a-z0-9]+)$/.test(search.resume)
      ? { resume: search.resume }
      : {}),
    ...(search.card === "saved" || search.card === "canceled" ? { card: search.card } : {}),
  }),
  loader: ({ context: { queryClient, yard }, params }) => {
    // What the screen asks for once it is drawn is asked for now, beside the Project, so it
    // is not a second round trip after the first paint.
    void queryClient.prefetchQuery(changeOrdersQuery(params.id));
    void queryClient.prefetchQuery(billingSummaryQuery);
    if (yard.kind === "builder") void queryClient.prefetchQuery(rfqsQuery);
    return queryClient.ensureQueryData(projectQuery(params.id));
  },
  component: ProjectDetail,
});

const inFlight = new Set(["queued", "running", "in_review"]);

function ProjectDetail() {
  const { id } = Route.useParams();
  const { yard } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // A Builder sends a ready takeoff to yards instead of quoting it.
  const builder = yard.kind === "builder";
  const { data: rfqs } = useQuery({ ...rfqsQuery, enabled: builder });
  const projectRfqs = rfqs?.rfqs.filter((rfq) => rfq.projectId === id) ?? [];

  const { data } = useSuspenseQuery({
    ...projectQuery(id),
    // While a Takeoff is being read or reviewed, keep the status fresh.
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => inFlight.has(run.status)) ? 4000 : false,
  });
  const { project, customer, planSets, runs, quotes } = data;

  const setStatus = useMutation({
    mutationFn: (status: "active" | "won" | "lost" | "archived") =>
      unwrap(api.projects[":id"].$patch({ param: { id }, json: { status } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
    onError: (error) => toast.error(error.message),
  });

  const createQuote = useMutation({
    mutationFn: (runId: string) => unwrap(api.quotes.$post({ json: { runId } })),
    onSuccess: async ({ quote, matched, lineCount }) => {
      await queryClient.invalidateQueries({ queryKey: ["projects", id] });
      await queryClient.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(`Quote #${quote.number} drafted`, {
        description: `${matched} of ${lineCount} lines matched to your catalog.`,
      });
      await navigate({ to: "/quotes/$id", params: { id: quote.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  // A start picked up after adding a card says so when it goes through.
  const announceStart = useRef(false);
  const startRun = useMutation({
    mutationFn: (planSetId: string) => unwrap(api.takeoffs.$post({ json: { planSetId } })),
    onSuccess: async ({ run }) => {
      if (announceStart.current) {
        announceStart.current = false;
        toast.success("Card saved. Your takeoff has started.", {
          description: "We will email you when it is ready.",
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      await navigate({ to: "/projects/$id/takeoffs/$runId", params: { id, runId: run.id } });
    },
    onError: (error, planSetId) => {
      announceStart.current = false;
      // Refused for want of a card: ask for one, never an error toast.
      const card = refusedStart(error, {
        kind: "takeoff",
        amountCents: billing?.summary.nextQuote.amountCents ?? 0,
        projectId: id,
        planSetId,
      });
      if (card) setCardFor({ purpose: card, planSetId });
      else toast.error(error.message);
    },
  });

  // Drawings that were uploaded but never read, for example after a failed start.
  // A Change Order's drawings are started from its own screen.
  const unstarted = planSets.filter(
    (set) =>
      set.status !== "uploading" &&
      !set.changeOrderId &&
      !runs.some((run) => run.planSetId === set.id),
  );

  // A Change Order's run lives on its own screen, under Change Orders.
  const takeoffRuns = runs.filter((run) => !run.changeOrderId);
  const beingReviewed = runs.some((run) => run.status === "in_review");
  const { data: changeOrderData } = useQuery({
    ...changeOrdersQuery(id),
    refetchInterval: (query) =>
      query.state.data?.changeOrders.some(
        (order) => order.status === "running" || order.status === "in_review",
      )
        ? 4000
        : false,
  });
  const changeOrders = changeOrderData?.changeOrders ?? [];
  const hasReleased = takeoffRuns.some((run) => run.status === "released");
  const [openingChangeOrder, setOpeningChangeOrder] = useState(false);
  const { data: billing } = useQuery(billingSummaryQuery);
  const changeOrderFee = billing?.summary.nextChangeOrder.amountCents;

  /* ---- A card at the moment it is needed ---------------------------------- */

  const search = Route.useSearch();
  const [cardFor, setCardFor] = useState<{ purpose: CardPurpose; planSetId?: string } | null>(null);
  const returnTo = (target: string) => `/projects/${id}?resume=${target}`;

  /** Starts the takeoff, or first asks for the card it will be charged to. */
  function startTakeoff(planSetId: string) {
    if (cardNeeded(billing?.summary, "takeoff")) {
      setCardFor({
        purpose: {
          kind: "takeoff",
          amountCents: billing?.summary.nextQuote.amountCents ?? 0,
          projectId: id,
          planSetId,
        },
        planSetId,
      });
      return;
    }
    startRun.mutate(planSetId);
  }

  /** Change orders are paid, so the card is asked for before one is opened. */
  function newChangeOrder() {
    if (cardNeeded(billing?.summary, "change_order")) {
      setCardFor({
        purpose: { kind: "change_order", amountCents: changeOrderFee ?? 0, projectId: id },
      });
      return;
    }
    setOpeningChangeOrder(true);
  }

  const clearResume = () =>
    navigate({ to: "/projects/$id", params: { id }, search: {}, replace: true });

  /** What the Member was doing before the card: open the change order, or start the takeoff. */
  function resume(target: string, afterCard: boolean) {
    if (target === "change-order") {
      setOpeningChangeOrder(true);
      if (afterCard) {
        toast.success("Card saved", { description: "Name the change order to open it." });
      }
      return;
    }
    const existing = runs.find((run) => run.planSetId === target);
    if (existing) {
      void navigate({ to: "/projects/$id/takeoffs/$runId", params: { id, runId: existing.id } });
      return;
    }
    announceStart.current = afterCard;
    startRun.mutate(target);
  }

  // Back from Stripe: wait until the card shows (the webhook can lag), then carry on.
  const cardReturn = useCardReturn({
    active: search.resume !== undefined && search.card !== undefined,
    card: search.card,
    onReady: () => {
      const target = search.resume;
      void clearResume();
      if (target) resume(target, true);
    },
    onCanceled: () => {
      void clearResume();
      toast.info("No card was added", {
        description: "Your drawings are saved. Start when you are ready.",
      });
    },
  });

  // From the owners' email link: ask for the card, or carry on when there already is one.
  const pickedUp = useRef(false);
  useEffect(() => {
    const target = search.resume;
    if (!target || search.card !== undefined || !billing || pickedUp.current) return;
    pickedUp.current = true;
    const kind = target === "change-order" ? "change_order" : "takeoff";
    if (cardNeeded(billing.summary, kind)) {
      setCardFor(
        target === "change-order"
          ? {
              purpose: {
                kind: "change_order",
                amountCents: billing.summary.nextChangeOrder.amountCents,
                projectId: id,
              },
            }
          : {
              purpose: {
                kind: "takeoff",
                amountCents: billing.summary.nextQuote.amountCents,
                projectId: id,
                planSetId: target,
              },
              planSetId: target,
            },
      );
      return;
    }
    void clearResume();
    resume(target, false);
  });
  // The fee itself, however much of it account credit pays.
  const fromCredit = billing?.summary.nextChangeOrder.fromAccountCreditCents ?? 0;
  const changeOrderFeeCopy =
    changeOrderFee === 0 && fromCredit === 0
      ? "No charge for change orders on your billing plan."
      : `${changeOrderFee === undefined ? "$100" : money(changeOrderFee + fromCredit)} per released change order${fromCredit > 0 ? ", paid from your account credit while it lasts" : ""}.`;

  const address = [
    project.siteAddress?.line1,
    project.siteAddress?.city,
    project.siteAddress?.region,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <Crumbs trail={[{ label: "Projects", to: "/projects" }, { label: project.name }]} />

      {cardReturn.waiting ? (
        <Container className="flex items-start gap-x-3 px-6 py-4">
          <span className="mt-0.5 text-ui-fg-subtle">
            <CreditCard />
          </span>
          <div className="min-w-0">
            <p className="txt-compact-small-plus text-ui-fg-base">
              {cardReturn.timedOut ? "Still waiting for your card" : "Saving your card"}
            </p>
            <p className="txt-small mt-0.5 text-pretty text-ui-fg-subtle">
              {cardReturn.timedOut
                ? "It can take a minute to come through from Stripe. This page keeps checking, and carries on by itself once it does."
                : search.resume === "change-order"
                  ? "This takes a few seconds. Then you can name the change order."
                  : "This takes a few seconds. Then your takeoff starts by itself."}
            </p>
          </div>
        </Container>
      ) : null}

      {beingReviewed ? (
        <Container className="flex items-start gap-x-3 px-6 py-4">
          <span className="mt-0.5 text-ui-tag-orange-icon">
            <UserGroup />
          </span>
          <div className="min-w-0">
            <p className="txt-compact-small-plus text-ui-fg-base">
              Our Reviewers are checking your takeoff
            </p>
            <p className="txt-small mt-0.5 text-ui-fg-subtle">
              They check the lines against your drawings before the takeoff reaches you. We will
              email you as soon as it is ready. There is nothing you need to do in the meantime.
            </p>
          </div>
        </Container>
      ) : null}

      <TwoColumn
        main={
          <>
            <Container className="divide-y p-0">
              <SectionHeader
                level="h1"
                title={project.name}
                description={`Created ${date(project.createdAt)}`}
                actions={
                  <>
                    <ProjectStatus status={project.status} />
                    <DropdownMenu>
                      <DropdownMenu.Trigger asChild>
                        <IconButton aria-label="Project actions">
                          <EllipsisHorizontal />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content align="end">
                        <DropdownMenu.Label>Mark as</DropdownMenu.Label>
                        {(["active", "won", "lost", "archived"] as const)
                          .filter((status) => status !== project.status)
                          .map((status) => (
                            <DropdownMenu.Item
                              key={status}
                              onSelect={() => setStatus.mutate(status)}
                            >
                              <span className="capitalize">{status}</span>
                            </DropdownMenu.Item>
                          ))}
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </>
                }
              />
            </Container>

            <Container className="divide-y p-0">
              <SectionHeader
                title="Takeoffs"
                description="Each issue of the drawings is read into its own takeoff."
              />
              {takeoffRuns.length === 0 && unstarted.length === 0 ? null : (
                <Table>
                  <Table.Body>
                    {unstarted.map((set) => (
                      <Table.Row key={set.id}>
                        <Table.Cell>
                          <div className="flex items-center gap-x-3 py-2">
                            <Photo className="shrink-0 text-ui-fg-muted" />
                            <div className="min-w-0 flex-1">
                              <div className="txt-compact-small-plus truncate text-ui-fg-base">
                                {set.label}
                              </div>
                              <div className="txt-compact-xsmall text-ui-fg-muted">
                                Uploaded {ago(set.createdAt)}, not read yet
                              </div>
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="w-[1%] whitespace-nowrap text-right" colSpan={2}>
                          <Button
                            size="small"
                            isLoading={startRun.isPending && startRun.variables === set.id}
                            onClick={() => startTakeoff(set.id)}
                          >
                            Start takeoff
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {takeoffRuns.map((run) => {
                      const set = planSets.find((candidate) => candidate.id === run.planSetId);
                      const hasQuote = quotes.length > 0;
                      return (
                        <Table.Row
                          key={run.id}
                          data-clickable="true"
                          onClick={() =>
                            navigate({
                              to: "/projects/$id/takeoffs/$runId",
                              params: { id, runId: run.id },
                            })
                          }
                        >
                          <Table.Cell>
                            <div className="flex items-center gap-x-3 py-2">
                              <DocumentText className="shrink-0 text-ui-fg-muted" />
                              <div className="min-w-0 flex-1">
                                <div className="txt-compact-small-plus truncate text-ui-fg-base">
                                  {set?.label ?? "Drawings"}
                                </div>
                                {inFlight.has(run.status) ? (
                                  <div className="mt-1.5 flex max-w-xs items-center gap-x-2">
                                    <ProgressBar value={run.progress} className="h-1" />
                                    <span className="txt-compact-xsmall shrink-0 text-ui-fg-muted">
                                      {stageLabels[run.stage] ?? run.stage}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="txt-compact-xsmall text-ui-fg-muted">
                                    {run.status === "failed"
                                      ? (run.error ?? "Reading failed. You were not charged.")
                                      : run.status === "canceled"
                                        ? "Canceled. Nothing was charged."
                                        : run.status === "released"
                                          ? `Ready ${ago(run.releasedAt ?? run.createdAt)}`
                                          : `Started ${ago(run.createdAt)}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="w-[1%] whitespace-nowrap">
                            <CustomerRunStatus status={run.status} />
                          </Table.Cell>
                          <Table.Cell className="w-[1%] whitespace-nowrap text-right max-sm:hidden">
                            {run.status === "released" && builder ? (
                              <Button
                                size="small"
                                variant={projectRfqs.length > 0 ? "secondary" : "primary"}
                                asChild
                              >
                                <Link
                                  to="/rfqs/new"
                                  search={{ runId: run.id }}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  Request quotes
                                </Link>
                              </Button>
                            ) : run.status === "released" ? (
                              <Button
                                size="small"
                                variant={hasQuote ? "secondary" : "primary"}
                                isLoading={
                                  createQuote.isPending && createQuote.variables === run.id
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  createQuote.mutate(run.id);
                                }}
                              >
                                {hasQuote ? "New quote" : "Create quote"}
                              </Button>
                            ) : null}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              )}
              {/* Once a takeoff is released, dropped drawings are asked about: a change order, or another building on this site. */}
              <UploadDrawings
                key={hasReleased ? "released" : "first"}
                projectId={id}
                revisionNumber={planSets.length + 1}
                released={hasReleased}
                nextChangeOrderNumber={
                  Math.max(0, ...changeOrders.map((order) => order.number)) + 1
                }
              />
            </Container>

            {/* Nothing to do with change orders before a takeoff is released: the section waits until then. */}
            {hasReleased || changeOrders.length > 0 ? (
              <Container className="divide-y p-0">
                <SectionHeader
                  title="Change Orders"
                  description="Revised drawings after a takeoff was released, read again and shown as what changed."
                  actions={
                    hasReleased ? (
                      <Button size="small" onClick={newChangeOrder}>
                        <Plus /> New change order
                      </Button>
                    ) : null
                  }
                />
                {changeOrders.length === 0 ? (
                  <EmptyState
                    className="py-10"
                    title="No change orders yet"
                    description={`Drawings changed after the takeoff was released? Start a change order: upload the revised sheets, we read them, and you see exactly what changed. ${changeOrderFeeCopy}`}
                  />
                ) : (
                  <Table>
                    <Table.Body>
                      {changeOrders.map((order) => (
                        <Table.Row
                          key={order.id}
                          data-clickable="true"
                          onClick={() =>
                            navigate({
                              to: "/projects/$id/change-orders/$coId",
                              params: { id, coId: order.id },
                            })
                          }
                        >
                          <Table.Cell className="py-2">
                            {/* Wraps rather than truncates: a long title must not push the status off a phone. */}
                            <div className="txt-compact-small-plus text-pretty break-words text-ui-fg-base">
                              Change Order {order.number}: {order.title}
                            </div>
                            <div className="txt-compact-xsmall text-ui-fg-muted">
                              {order.status === "released"
                                ? `Released ${ago(order.releasedAt ?? order.updatedAt)}`
                                : order.status === "running" && order.runStage
                                  ? (stageLabels[order.runStage] ?? "Reading")
                                  : `Opened ${ago(order.createdAt)}`}
                            </div>
                          </Table.Cell>
                          <Table.Cell className="w-[1%] whitespace-nowrap">
                            <ChangeOrderStatus status={order.status} />
                          </Table.Cell>
                          <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base max-sm:hidden">
                            {order.feeCents === null
                              ? ""
                              : order.feeCents === 0
                                ? "No charge"
                                : money(order.feeCents)}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </Container>
            ) : null}

            {builder ? (
              <Container className="divide-y p-0">
                <SectionHeader title="RFQs" />
                {projectRfqs.length === 0 ? (
                  <EmptyState
                    className="py-10"
                    title="No requests for quotes yet"
                    description="When a takeoff is ready, send it to the yards you choose and compare their quotes here."
                  />
                ) : (
                  <Table>
                    <Table.Body>
                      {projectRfqs.map((rfq) => (
                        <Table.Row
                          key={rfq.id}
                          data-clickable="true"
                          onClick={() => navigate({ to: "/rfqs/$id", params: { id: rfq.id } })}
                        >
                          <Table.Cell>
                            <span className="txt-compact-small-plus text-ui-fg-base">
                              {rfq.recipients.total} {rfq.recipients.total === 1 ? "yard" : "yards"}
                            </span>
                            <span className="ml-2 text-ui-fg-muted max-sm:hidden">
                              {date(rfq.createdAt)}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="w-[1%] whitespace-nowrap">
                            <RfqStatus status={rfq.status} />
                          </Table.Cell>
                          <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base">
                            {rfq.lowestTotalCents !== null ? money(rfq.lowestTotalCents) : "-"}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </Container>
            ) : (
              <Container className="divide-y p-0">
                <SectionHeader title="Quotes" />
                {quotes.length === 0 ? (
                  <EmptyState
                    className="py-10"
                    title="No quotes yet"
                    description="When a takeoff is ready, create a quote from it. It is priced from your catalog."
                  />
                ) : (
                  <Table>
                    <Table.Body>
                      {quotes.map((quote) => (
                        <Table.Row
                          key={quote.id}
                          data-clickable="true"
                          onClick={() => navigate({ to: "/quotes/$id", params: { id: quote.id } })}
                        >
                          <Table.Cell>
                            <span className="txt-compact-small-plus text-ui-fg-base">
                              Quote #{quote.number}
                            </span>
                            <span className="ml-2 text-ui-fg-muted max-sm:hidden">
                              {date(quote.createdAt)}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="w-[1%] whitespace-nowrap">
                            <QuoteStatus status={quote.status} />
                          </Table.Cell>
                          <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base">
                            {money(quote.totalCents, quote.currency)}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </Container>
            )}
          </>
        }
        side={
          <>
            {builder ? null : <ProjectCustomer projectId={id} customer={customer} />}

            <Container className="divide-y p-0">
              <SectionHeader title="Site" />
              <SectionRow label="Address">{address || null}</SectionRow>
              <SectionRow label="Notes">{project.notes}</SectionRow>
            </Container>
          </>
        }
      />

      {cardFor ? (
        <AddCardDialog
          open
          onOpenChange={(open) => {
            if (open) return;
            setCardFor(null);
            if (search.resume) void clearResume();
          }}
          purpose={cardFor.purpose}
          returnTo={returnTo(
            cardFor.purpose.kind === "change_order" ? "change-order" : (cardFor.planSetId ?? ""),
          )}
          onContinueWithoutCard={
            cardFor.purpose.kind === "change_order" ? () => setOpeningChangeOrder(true) : undefined
          }
        />
      ) : null}

      <Drawer open={openingChangeOrder} onOpenChange={setOpeningChangeOrder}>
        <Drawer.Content>
          {openingChangeOrder ? (
            <NewChangeOrderForm
              projectId={id}
              nextNumber={changeOrders.length + 1}
              feeCopy={changeOrderFeeCopy}
              onClose={() => setOpeningChangeOrder(false)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

/** Opens a Change Order, then goes to its screen to upload the revised drawings. */
function NewChangeOrderForm({
  projectId,
  nextNumber,
  feeCopy,
  onClose,
}: {
  projectId: string;
  nextNumber: number;
  feeCopy: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: (form: FormData) =>
      unwrap(
        api["change-orders"].$post({
          json: {
            projectId,
            title: String(form.get("title") ?? "").trim(),
            description: String(form.get("description") ?? "").trim() || null,
          },
        }),
      ),
    onSuccess: async ({ changeOrder }) => {
      await queryClient.invalidateQueries({ queryKey: ["change-orders"] });
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId] });
      onClose();
      await navigate({
        to: "/projects/$id/change-orders/$coId",
        params: { id: projectId, coId: changeOrder.id },
      });
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Change Order {nextNumber}</Drawer.Title>
        <Drawer.Description>
          Name what changed. You upload the revised drawings on the next screen. {feeCopy}
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Title" hint="For example: Revised roof framing, or Garage added.">
          {(control) => <Input {...control} name="title" required maxLength={200} autoFocus />}
        </Field>
        <Field
          label="What changed"
          optional
          hint="In your own words, for your team and our Reviewers."
        >
          {(control) => <Textarea {...control} name="description" rows={4} maxLength={5000} />}
        </Field>
      </Drawer.Body>
      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={create.isPending}>
          Open change order
        </Button>
      </Drawer.Footer>
    </form>
  );
}
